import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';

import {
  FilterFieldMapping,
  ReportGroupsFilterOptions
} from '../../report-groups-filter-criteria/report-groups-filter-criteria.types';
import {
  CreateFilterOptions,
  DynamicFilterOptions,
  ReportGroupsDynamicFilter
} from './report-groups-dynamic-filter.types';
import { UdfApiResponse, GroupCriteriaControlValue } from '../../../report-groups.types';
import {
  ContainsOpFilter,
  deviceStatusDefaultItems,
  deviceTypeDefaultItems,
  DoesNotContainOpFilter,
  DoesNotEqualOpFilter,
  EarlierThanOpFilter,
  EarlierThanOrEqualToOpFilter,
  EqualsOpFilter,
  GreaterThanOpFilter,
  GreaterThanOrEqualToOpFilter,
  InOpFilter,
  LaterThanOpFilter,
  LaterThanOrEqualToOpFilter,
  LessThanOpFilter,
  LessThanOrEqualToOpFilter,
  modelTypeValueItems,
  NotInOpFilter
} from './report-groups-dynamic-filter.const';
import { ReportGroupsFilterFactoryService } from './report-groups-filter-factory.service';

@Injectable({
  providedIn: 'root'
})
export class ReportGroupsDynamicFilterService {
  readonly #fb = inject(FormBuilder);
  readonly #filterFactory = inject(ReportGroupsFilterFactoryService);

  isCheckboxControl(controlName: string): boolean {
    return this.isDeviceTypeCheckboxControl(controlName) || this.isDeviceStatusTypeCheckboxControl(controlName);
  }

  getDeviceTypeMappedValueForApi(deviceType: string): string {
    return this.deviceTypeMapper[deviceType] || deviceType;
  }

  getGroupCriteriaControlValueFromApi(
    fieldName: string,
    values: GroupCriteriaControlValue
  ): GroupCriteriaControlValue | AbstractControl {
    // todo: add ReportGroupsDynamicFilter#createControlValueFromApi callback to all control types and remove if-else
    // todo: remove if condition when createControlValueFromApi is implemented for all the ReportGroupsDynamicFilter
    //  The condition was added to limit the refactoring to work for ModelType only as it has a correct createControlValueFromApi
    const filter = this.findFilter(fieldName) as ReportGroupsDynamicFilter;
    if (filter.createControlValueFromApiPayload) {
      return filter?.createControlValueFromApiPayload(values as string[]) as GroupCriteriaControlValue;
    }

    if (this.isDeviceTypeCheckboxControl(fieldName)) {
      const mappedValues = (values as DynamicFilterOptions[]).map(item => {
        const key = Object.keys(this.deviceTypeMapper).find(key => this.deviceTypeMapper[key] === item);
        if (key) {
          return key;
        }
        return item;
      });
      return this.createCheckboxDynamicFilterControl({
        fieldName,
        defaultItem: mappedValues as DynamicFilterOptions[],
        items: deviceTypeDefaultItems
      });
    } else if (this.isDeviceStatusTypeCheckboxControl(fieldName)) {
      return this.createCheckboxDynamicFilterControl({
        fieldName,
        defaultItem: values as DynamicFilterOptions[],
        items: deviceStatusDefaultItems
      });
    } else if (values.length > 1) {
      return [values] as string[];
    } else {
      return values[0] as string;
    }
  }

  public updateFilterRegistryWithUdf(udf: UdfApiResponse[]): void {
    const indexPositionBeforeLassoControl = this.filtersRegistry.length - 1;
    const udfFilters = udf.map(({ name }) =>
      this.#filterFactory.createTextFilter({
        fieldName: name,
        operatorItems: [
          ContainsOpFilter,
          DoesNotContainOpFilter,
          DoesNotEqualOpFilter,
          EqualsOpFilter,
          GreaterThanOpFilter,
          GreaterThanOrEqualToOpFilter,
          InOpFilter,
          LessThanOpFilter,
          LessThanOrEqualToOpFilter
        ],
        defaultOperatorItems: EqualsOpFilter
      })
    );

    this.filtersRegistry.splice(indexPositionBeforeLassoControl, 0, ...udfFilters);
  }

  public findFilter(filterName: string | null): ReportGroupsDynamicFilter | null {
    if (filterName === 'Select' || !filterName) {
      return null;
    }

    const filter = this.filtersRegistry.find(filter => filter.fieldName === filterName);
    if (!filter) {
      throw new Error("NotFound: Component was not found in the ReportGroupsDynamicFilterService's registry");
    }

    return filter;
  }

  public readonly filtersRegistry = [
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.CustomerNumber.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.CustomerType.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.AccountNumber.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.Cycle.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.Book.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.RateClass.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.RevenueClass.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceType.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.CommDeviceId.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createMultiselectFilter({
      fieldName: FilterFieldMapping.ModelType.fieldName as string,
      operatorItems: [InOpFilter, NotInOpFilter],
      defaultOperatorItems: InOpFilter,
      valueItems: modelTypeValueItems,
      selectedValueItems: []
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.SerialNumber.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.AccountStatusChangeDate.fieldName as string,
      operatorItems: [
        DoesNotEqualOpFilter,
        EqualsOpFilter,
        LaterThanOpFilter,
        LaterThanOrEqualToOpFilter,
        InOpFilter,
        EarlierThanOpFilter,
        EarlierThanOrEqualToOpFilter
      ],
      defaultOperatorItems: LaterThanOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceEndDate.fieldName as string,
      operatorItems: [
        DoesNotEqualOpFilter,
        EqualsOpFilter,
        LaterThanOpFilter,
        LaterThanOrEqualToOpFilter,
        InOpFilter,
        EarlierThanOpFilter,
        EarlierThanOrEqualToOpFilter
      ],
      defaultOperatorItems: LaterThanOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceStartDate.fieldName as string,
      operatorItems: [
        DoesNotEqualOpFilter,
        EqualsOpFilter,
        LaterThanOpFilter,
        LaterThanOrEqualToOpFilter,
        InOpFilter,
        EarlierThanOpFilter,
        EarlierThanOrEqualToOpFilter
      ],
      defaultOperatorItems: LaterThanOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.DateLastAddedUTC.fieldName as string,
      operatorItems: [
        DoesNotEqualOpFilter,
        EqualsOpFilter,
        LaterThanOpFilter,
        LaterThanOrEqualToOpFilter,
        InOpFilter,
        EarlierThanOpFilter,
        EarlierThanOrEqualToOpFilter
      ],
      defaultOperatorItems: LaterThanOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.PortNumber.fieldName as string,
      operatorItems: [
        DoesNotEqualOpFilter,
        EqualsOpFilter,
        GreaterThanOpFilter,
        GreaterThanOrEqualToOpFilter,
        InOpFilter,
        LessThanOpFilter,
        LessThanOrEqualToOpFilter
      ],
      defaultOperatorItems: EqualsOpFilter
    }),
    this.#filterFactory.createRadiobuttonFilter({
      fieldName: FilterFieldMapping.AccountStatus.fieldName as string,
      items: [DynamicFilterOptions.Active, DynamicFilterOptions.Inactive],
      defaultItem: DynamicFilterOptions.Active
    }),
    this.#filterFactory.createRadiobuttonFilter({
      fieldName: FilterFieldMapping.PriorityTypeCritical.fieldName as string,
      items: [DynamicFilterOptions.True, DynamicFilterOptions.False],
      defaultItem: DynamicFilterOptions.True
    }),
    this.#filterFactory.createRadiobuttonFilter({
      fieldName: FilterFieldMapping.PriorityTypesOthers.fieldName as string,
      items: [DynamicFilterOptions.True, DynamicFilterOptions.False],
      defaultItem: DynamicFilterOptions.True
    }),
    this.#filterFactory.createRadiobuttonFilter({
      fieldName: FilterFieldMapping.ServiceStatus.fieldName as string,
      items: [DynamicFilterOptions.Connected, DynamicFilterOptions.Disconnected],
      defaultItem: DynamicFilterOptions.Connected
    }),
    this.#filterFactory.createCommodityRadiobuttonFilter(),
    this.#filterFactory.createCheckboxFilter({
      fieldName: FilterFieldMapping.Status.fieldName as string,
      items: deviceStatusDefaultItems,
      defaultItem: DynamicFilterOptions.Active
    }),
    this.#filterFactory.createCheckboxFilter({
      fieldName: FilterFieldMapping.DeviceType.fieldName as string,
      items: deviceTypeDefaultItems,
      defaultItem: DynamicFilterOptions.None
    }),
    this.#filterFactory.createLassoFilter(),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.Model.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter],
      defaultOperatorItems: ContainsOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceAddress1.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceAddress2.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceCity.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceState.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    }),
    this.#filterFactory.createTextFilter({
      fieldName: FilterFieldMapping.ServiceZipCode.fieldName as string,
      operatorItems: [ContainsOpFilter, DoesNotContainOpFilter, DoesNotEqualOpFilter, EqualsOpFilter, InOpFilter],
      defaultOperatorItems: InOpFilter
    })
  ];

  public createCheckboxDynamicFilterControl({ fieldName, items, defaultItem }: CreateFilterOptions): AbstractControl {
    return this.#filterFactory
      .createCheckboxFilter({
        fieldName,
        items,
        defaultItem
      })
      .controls[0]().ctrl;
  }

  public createCommodityFormGroup({ removeButtonDisabled } = { removeButtonDisabled: false }): FormGroup {
    const {
      controls: [getValueCtrlFn, getOperatorFn, getOptionsFn]
    } = this.findFilter(FilterFieldMapping.Commodity.fieldName)!;
    const { ctrl: commodityControl } = getValueCtrlFn();
    const { ctrl: operatorControl } = getOperatorFn();
    const { ctrl: optionsControl } = getOptionsFn();
    return this.#fb.group({
      name: FilterFieldMapping.Commodity,
      operator: [operatorControl.value],
      values: this.getGroupCriteriaControlValueFromApi(FilterFieldMapping.Commodity.value as string, [
        commodityControl.value
      ]),
      options: [
        {
          ...optionsControl.value,
          removeButtonDisabled
        } as ReportGroupsFilterOptions
      ]
    });
  }

  private isDeviceTypeCheckboxControl(controlName: string): boolean {
    return controlName === FilterFieldMapping.DeviceType.value;
  }

  private isDeviceStatusTypeCheckboxControl(controlName: string): boolean {
    return controlName === FilterFieldMapping.Status.value;
  }

  private readonly deviceTypeMapper: {
    [key: string]: string;
  } = {
    ExactaGuard: 'ExactaGuardGateway',
    RMD: 'RMDSensor'
  };
}
